/**
 *
 * FBAppointmentAnswer
 *
 */

import React, { memo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// styles
import './style.scss';

// antd component
import { Form, Radio, Checkbox, Button } from 'antd';

// layout components
import ErrorBoundary from 'engine/ErrorBoundary';
import Preloader from 'components/Preloader';

// components
import HiddenField from 'components/Forms/Fields/HiddenField';
// inner communication components 
import {
  FBMobileWrapper,
} from 'mobile/components/Communications';

// custom react-multi-date-picker
import { DateObject } from 'react-multi-date-picker';
import { timeFormat, dateFormat } from 'utils/moment/moment.constant';

// icons
import CustomIcons from 'components/CustomIcons';


function FBAppointmentAnswer({
  form,
  showHeader,
  showAdminAnswer,
  showWrapper,
  type,
  appointments,
  values,
  actionRequired,
  loading,
  adminAnswer,
  hideActions,
  // actions
  onSubmit,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames('tcf-fb-appointment-group-preview', 'tcf-fb-group-answer', className);

  const [previewAppointment] = Form.useForm();
  const mainForm = !!form && form || previewAppointment;
  const formName = type;

  const [answers, setAnswers] = useState(values || []);
  const [formRequired, setFormRequired] = useState(true);

  // Refresh current value based on parent`s states
  useEffect(() => {
    if (!!values && JSON.stringify(values) != JSON.stringify(answers)) {
      setAnswers(values || []);
    } else if (!!!values) {
      setAnswers({});
      mainForm.resetFields();
    }
  }, [values]);

  const formatDateTimeValue = useCallback((value, format) => {
    if (!!!value) {
      return '';
    }

    if (typeof value == 'object') {
      return !!value && value.format(format);
    } else {
      return !!value && new DateObject(value).format(format);
    }

  }, [])

  const prepearGroup = useCallback((fields, type) => {
    let gruopOptions = [];
    if (!!fields?.['field-input'] && type) {
      gruopOptions = fields?.['field-input']?.map((opt, indx) => {
        const timeFrom = formatDateTimeValue(opt?.[`time-from`], timeFormat);
        const timeTo = formatDateTimeValue(opt?.[`time-to`], timeFormat);
        const label = `${timeFrom} - ${timeTo}`;
        return timeFrom && timeTo && {
          label,
          value: `${opt?.[`time-from`]} - ${opt?.[`time-to`]}`,
          key: indx
        }
      }).filter(el => !!el && Object.keys(el)?.length > 0)
    }
    return gruopOptions;
  }, [appointments])

  const generatePreviewByType = useCallback((row, name) => {
    switch (row['field-type']) {
      case 'checkbox':
        const checkboxOptions = prepearGroup(row, 'checkbox')

        return checkboxOptions?.length > 0 && (
          <>
            <Form.Item
              name={[name, [`${row['field-type']}-answer`]]}
              key={[name, [`${row['field-type']}-answer`]]}
              className="forms-field"
              rules={[
                {
                  required: formRequired,
                  message: 'Champ obligatoire',
                },
              ]}
            >
              <Checkbox.Group
                options={checkboxOptions}
              />
            </Form.Item>
          </>
        );

      case 'radio':
        const radioOptions = prepearGroup(row, 'radio')
        return radioOptions?.length > 0 && (
          <>
            <Form.Item
              name={[name, [`${row['field-type']}-answer`]]}
              key={[name, [`${row['field-type']}-answer`]]}
              className="forms-field"
              rules={[
                {
                  required: formRequired,
                  message: 'Champ obligatoire',
                },
              ]}
            >
              <Radio.Group
                options={radioOptions}
              />
            </Form.Item>
          </>
        );

      default: <></>;
    }
  }, [values, answers, formRequired])

  // Update parent states based on changes inside Form
  const handleValuesChange = useCallback((changedValues, allValues) => {
    // console.log('handleValuesChange', { changedValues, allValues, answers });
    const changedVals = Object.values(changedValues);
    const hasAnyAnswer = changedVals?.some(val => val?.['checkbox-answer']?.length > 0 || val?.['radio-answer']?.length > 0);
    !!hasAnyAnswer ? setFormRequired(false) : setFormRequired(true);
  }, [])

  const onSubmitHandle = useCallback((data) => {
    setAnswers(data);
    !!onSubmit && !formRequired && onSubmit(data, formName);
  }, [values, formRequired]);

  const onFinishFailed = errorInfo => {
    console.error('Failed FBAppointmentAnswer:', errorInfo);
  };

  const RenderComponent = ({children}) => !!showWrapper ? (
    <>
      <FBMobileWrapper
        key="appointments-wrapper"
        className={childClassNames}
        showHeader={showHeader}
        showAdminAnswer={showAdminAnswer}
        adminAnswer={adminAnswer}
        types={["appointments"]}
        loading={loading}
      >
        <ErrorBoundary>
          {children}
        </ErrorBoundary>
      </FBMobileWrapper>
    </>
  ) : (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  );

  return !!appointments && (
      <RenderComponent>
        <Form
          name={formName}
          form={mainForm}
          initialValues={{ ...answers }}
          className="tcf-fb-forms-answer"
          autoComplete="off"
          onValuesChange={handleValuesChange}
          onFinish={onSubmitHandle}
          onFinishFailed={onFinishFailed}
          disabled={loading || !!values || !actionRequired}
        >
          {showHeader && (
            <div className='form-header'>
              {!!appointments[`${type}-title`] && (
                <h3 className="form-title">{appointments[`${type}-title`]}</h3>
              )}
              {!!appointments[`${type}-description`] && (
                <p className="form-description">{appointments[`${type}-description`]}</p>
              )}
            </div>
          )}
          {appointments[`${type}-fields`]?.map((row, indx) => {
            const titleDate = formatDateTimeValue(row?.[`field-date`], dateFormat);
            return !!row && (
              <div key={`appointment-answ-row-${indx}`} className='form-row'>
                <h4 className={`form-row__title appointment ${row[`field-type`]}`}>
                  <CustomIcons className="date-icon" type="calendar" />
                  {titleDate}
                </h4>
                <div className='form-row__field'>
                  <HiddenField
                    key={[indx, `field-type`]}
                    data={{ name: [indx, `field-type`] }}
                    index={0}
                    value={row['field-type']}
                  />
                  <HiddenField
                    key={[indx, `field-date`]}
                    data={{ name: [indx, `field-date`] }}
                    index={0}
                    value={row?.['field-date']}
                  />
                  {generatePreviewByType(row, indx)}
                </div>
              </div>
            );
          })}
          {(!!!hideActions && !!actionRequired && !values) && (
            <div className="actions-wrapper">
              <Button className="btn-previous" onClick={() => {
                mainForm.resetFields();
                setAnswers({});
              }}>
                Annuler
              </Button>
              <Button
                className="confirm-btn"
                form={formName}
                htmlType="submit"
              >
                Envoyer
              </Button>
            </div>
          )}
        </Form>
      </RenderComponent>
  );
}

FBAppointmentAnswer.defaultProps = {
  type: 'appointments',
  form: null,
  actionRequired: true,
  showHeader: true,
  showWrapper: true,
  showAdminAnswer: false,
  hideActions: false,
};

FBAppointmentAnswer.propTypes = {
  appointments: PropTypes.oneOfType([PropTypes.object, PropTypes.bool, PropTypes.array]),
  values: PropTypes.oneOfType([PropTypes.object, PropTypes.bool, PropTypes.array]),
  onSubmit: PropTypes.func
};

export default memo(FBAppointmentAnswer);
